import React from "react";
import SearchIcon from "@mui/icons-material/Search";
import { isDoctorTransferEnabled } from "../../../../utils/clinicFeatures";

export type DoctorOption = { id: string; label: string };

interface AppointmentFilterProps {
  selectedOption: string;
  handleDay: (event: React.ChangeEvent<HTMLInputElement>) => void;
  all: string;
  handleOptionChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  sessions?: string[];
  selectedSession?: string;
  handleSessionChange?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => void;
  showSessionFilter?: boolean;
  selectedDate?: string;
  handleDateChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  variant?: "stacked" | "toolbar";
  doctors?: DoctorOption[];
  selectedDoctorId?: string;
  onDoctorChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  patientSearch?: string;
  onPatientSearchChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  hideStatusFilter?: boolean;
}

const statusFilters = [
  { value: "pending", label: "All", hide: false },
  { value: "schedule", label: "Schedule", hide: false },
  { value: "inqueue", label: "Inqueue", hide: false },
  { value: "ongoing", label: "Ongoing", hide: false },
  { value: "waiting", label: "Waiting", hide: false },
  { value: "completed", label: "Complete", hide: false },
  { value: "canceled", label: "Cancel", hide: false },
  { value: "assigned", label: "Assigned", hide: false, requiresDoctorTransfer: true },
  // kept but hidden
  { value: "walkin", label: "Walk-in", hide: true },
  { value: "noshow", label: "No Show", hide: true },
];

/**
 * Match column count to visible field "slots" so the row fills evenly with no
 * trailing dead space. Patient search counts as 2 slots (it spans 2 columns)
 * since it needs more room than the other compact fields.
 */
const TOOLBAR_LG_GRID_COLS_CLASS: Record<number, string> = {
  1: "lg:grid-cols-1",
  2: "lg:grid-cols-2",
  3: "lg:grid-cols-3",
  4: "lg:grid-cols-4",
  5: "lg:grid-cols-5",
  6: "lg:grid-cols-6",
};

const selectClassName =
  "w-full min-w-0 px-3 py-2 rounded-lg text-sm border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary cursor-pointer";

function FilterField({
  label,
  children,
  className = "",
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`min-w-0 ${className}`}>
      <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}

const PERIOD_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "tomorrow", label: "Tomorrow" },
  { value: "all", label: "All" },
] as const;

function PeriodToggle({
  all,
  handleDay,
}: {
  all: string;
  handleDay: (event: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <div className="flex w-fit gap-1 p-1 bg-slate-100 dark:bg-slate-700 rounded-lg">
      {PERIOD_OPTIONS.map(({ value, label }) => (
        <label
          key={value}
          className={`px-3 py-1.5 rounded-md text-sm font-medium cursor-pointer transition-all whitespace-nowrap ${
            all === value
              ? "bg-white dark:bg-slate-600 text-indigo-600 dark:text-indigo-300 shadow-sm"
              : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
          }`}
        >
          <input
            type="radio"
            name="day"
            value={value}
            onChange={handleDay}
            checked={all === value}
            className="sr-only"
          />
          {label}
        </label>
      ))}
    </div>
  );
}

function StatusFilters({
  selectedOption,
  handleOptionChange,
  compact = false,
}: {
  selectedOption: string;
  handleOptionChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  compact?: boolean;
}) {
  const doctorTransferEnabled = isDoctorTransferEnabled();
  return (
    <div
      className={
        compact
          ? "flex gap-1.5 overflow-x-auto pb-0.5 -mx-1 px-1 scrollbar-thin"
          : "flex flex-wrap gap-2"
      }
    >
      {statusFilters
        .filter(
          ({ hide, requiresDoctorTransfer }) =>
            !hide && (!requiresDoctorTransfer || doctorTransferEnabled)
        )
        .map(({ value, label }) => (
        <label
          key={value}
          className={`${compact ? "shrink-0" : ""} px-3 py-1.5 rounded-lg text-sm font-medium cursor-pointer transition-all border whitespace-nowrap ${
            selectedOption === value
              ? "bg-indigo-500 text-white border-indigo-500 shadow-sm"
              : "bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:border-indigo-300 dark:hover:border-indigo-500 hover:text-indigo-600 dark:hover:text-indigo-300"
          }`}
        >
          <input
            type="radio"
            value={value}
            checked={selectedOption === value}
            onChange={handleOptionChange}
            className="sr-only"
          />
          {label}
        </label>
      ))}
    </div>
  );
}

const AppointmentFilter: React.FC<AppointmentFilterProps> = ({
  selectedOption,
  handleDay,
  all,
  handleOptionChange,
  sessions,
  selectedSession = "",
  handleSessionChange,
  showSessionFilter = false,
  selectedDate = "",
  handleDateChange,
  variant = "stacked",
  doctors,
  selectedDoctorId = "",
  onDoctorChange,
  patientSearch = "",
  onPatientSearchChange,
  hideStatusFilter = false
}) => {
  const showSessionRow =
    showSessionFilter &&
    handleSessionChange != null &&
    sessions != null;

  const showDateRow = all === "all" && handleDateChange != null;
  const showDoctorRow = onDoctorChange != null && doctors != null;
  const showPatientSearchRow = onPatientSearchChange != null;

  const toolbarSlotCount =
    1 + // period
    (showDateRow ? 1 : 0) +
    (showDoctorRow ? 1 : 0) +
    (showSessionRow ? 1 : 0) +
    (showPatientSearchRow ? 2 : 0);
  const toolbarLgGridColsClass =
    TOOLBAR_LG_GRID_COLS_CLASS[toolbarSlotCount] ?? "lg:grid-cols-4";

  if (variant === "toolbar") {
    return (
      <div className="mb-3 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50/80 dark:bg-slate-900/30 p-2.5 md:p-3.5 space-y-2.5">
        <div
          className={`grid grid-cols-1 sm:grid-cols-2 ${toolbarLgGridColsClass} gap-2.5`}
        >
          <FilterField label="Period">
            <PeriodToggle all={all} handleDay={handleDay} />
          </FilterField>

          {showDateRow ? (
            <FilterField label="Date">
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={selectedDate}
                  onChange={handleDateChange}
                  className={`${selectClassName} flex-1`}
                />
                {selectedDate ? (
                  <button
                    type="button"
                    onClick={() =>
                      handleDateChange({
                        target: { value: "" },
                      } as React.ChangeEvent<HTMLInputElement>)
                    }
                    className="shrink-0 px-2.5 py-2 rounded-lg text-xs font-medium text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600 hover:border-indigo-300 dark:hover:border-indigo-500 transition-colors"
                    title="Clear date filter"
                  >
                    Clear
                  </button>
                ) : null}
              </div>
            </FilterField>
          ) : null}

          {showDoctorRow ? (
            <FilterField label="Doctor">
              <select
                name="doctorId"
                value={selectedDoctorId}
                onChange={onDoctorChange}
                className={selectClassName}
              >
                <option value="">All Doctors</option>
                {doctors.map((doc) => (
                  <option key={doc.id} value={doc.id}>
                    {doc.label}
                  </option>
                ))}
              </select>
            </FilterField>
          ) : null}

          {showSessionRow ? (
            <FilterField label="Session">
              <select
                name="session"
                value={selectedSession}
                onChange={handleSessionChange}
                className={selectClassName}
              >
                <option value="">All Sessions</option>
                {sessions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </FilterField>
          ) : null}

          {showPatientSearchRow ? (
            <FilterField label="Patient search" className="sm:col-span-2 lg:col-span-2">
              <div className="relative">
                <SearchIcon
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                  sx={{ fontSize: 18 }}
                />
                <input
                  type="search"
                  value={patientSearch}
                  onChange={onPatientSearchChange}
                  placeholder="Search by name, reg no, or mobile…"
                  className={`${selectClassName} pl-9 pr-9`}
                />
                {patientSearch ? (
                  <button
                    type="button"
                    onClick={() =>
                      onPatientSearchChange({
                        target: { value: "" },
                      } as React.ChangeEvent<HTMLInputElement>)
                    }
                    className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 rounded-md text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                    aria-label="Clear patient search"
                  >
                    Clear
                  </button>
                ) : null}
              </div>
            </FilterField>
          ) : null}
        </div>

        {!hideStatusFilter ? (
          <div>
            <span className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1.5">
              Status
            </span>
            <StatusFilters
              selectedOption={selectedOption}
              handleOptionChange={handleOptionChange}
              compact
            />
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-4 mb-6">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-slate-500 dark:text-slate-400">
          Period:
        </span>
        <PeriodToggle all={all} handleDay={handleDay} />
      </div>

      {showDateRow && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-slate-500 dark:text-slate-400">
            Date:
          </span>
          <input
            type="date"
            value={selectedDate}
            onChange={handleDateChange}
            className="px-3 py-1.5 rounded-lg text-sm font-medium border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
          {selectedDate ? (
            <button
              type="button"
              onClick={() =>
                handleDateChange({
                  target: { value: "" },
                } as React.ChangeEvent<HTMLInputElement>)
              }
              className="px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600 hover:border-indigo-300 dark:hover:border-indigo-500 hover:text-indigo-600 dark:hover:text-indigo-300 transition-colors"
            >
              Clear
            </button>
          ) : null}
        </div>
      )}

      {showSessionRow && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-slate-500 dark:text-slate-400">
            Session:
          </span>
          <div className="flex flex-wrap gap-2">
            <label
              className={`px-3 py-1.5 rounded-lg text-sm font-medium cursor-pointer transition-all border ${
                selectedSession === ""
                  ? "bg-indigo-500 text-white border-indigo-500 shadow-md"
                  : "bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:border-indigo-300 dark:hover:border-indigo-500 hover:text-indigo-600 dark:hover:text-indigo-300"
              }`}
            >
              <input
                type="radio"
                name="session"
                value=""
                checked={selectedSession === ""}
                onChange={handleSessionChange}
                className="sr-only"
              />
              All
            </label>
            {sessions.map((name) => (
              <label
                key={name}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium cursor-pointer transition-all border ${
                  selectedSession === name
                    ? "bg-indigo-500 text-white border-indigo-500 shadow-md"
                    : "bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:border-indigo-300 dark:hover:border-indigo-500 hover:text-indigo-600 dark:hover:text-indigo-300"
                }`}
              >
                <input
                  type="radio"
                  name="session"
                  value={name}
                  checked={selectedSession === name}
                  onChange={handleSessionChange}
                  className="sr-only"
                />
                {name}
              </label>
            ))}
          </div>
        </div>
      )}

      <StatusFilters
        selectedOption={selectedOption}
        handleOptionChange={handleOptionChange}
      />
    </div>
  );
};

export default React.memo(AppointmentFilter);
